import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from '@/composables/useToast'
import type {
  Family,
  CreateFamilyRequest,
  UpdateFamilyRequest,
  FamilyMember,
  CreateMemberRequest,
  UpdateMemberRequest,
  FamilyRole,
  ApiError
} from '@/types/api'
import {
  // Casos de uso de familias
  loadFamiliesUseCase,
  loadFamilyUseCase,
  createFamilyUseCase,
  updateFamilyUseCase,
  deleteFamilyUseCase,
  loadFamilyRolesUseCase,
  // Casos de uso de miembros
  loadFamilyMembersUseCase,
  createMemberUseCase,
  updateMemberUseCase,
  deleteMemberUseCase,
  // EP-26 (B7.5, mr-12): traduce errorCode del backend a un mensaje específico
  describeMemberRemovalError
} from '@pepi/core'

/**
 * Store de familias y miembros
 *
 * Solo orquesta llamadas a casos de uso de @pepi/core.
 * No contiene lógica de negocio - solo mantiene estado reactivo.
 */
export const useFamilyStore = defineStore('family', () => {
  // ============== COMPOSABLES ==============

  const toast = useToast()

  // ============== STATE ==============

  // Familias
  const families = ref<Family[]>([])
  const currentFamily = ref<Family | null>(null)
  const roles = ref<FamilyRole[]>([])

  // Miembros de la familia actual
  const currentFamilyMembers = ref<FamilyMember[]>([])

  // Estados de loading
  const loadingFamilies = ref(false)
  const loadingFamily = ref(false)
  const loadingMembers = ref(false)
  const loadingRoles = ref(false)

  // Estados de operaciones CRUD
  const creatingFamily = ref(false)
  const updatingFamily = ref(false)
  const deletingFamily = ref(false)
  const creatingMember = ref(false)
  const updatingMember = ref(false)
  const deletingMember = ref(false)

  // Estados de error
  const error = ref<ApiError | null>(null)

  // Estados de éxito (auto-limpieza después de 3 segundos)
  const successMessage = ref<string | null>(null)

  // ============== GETTERS ==============

  const hasFamilies = computed(() => families.value.length > 0)
  const familiesCount = computed(() => families.value.length)

  const hasCurrentFamily = computed(() => currentFamily.value !== null)
  const currentFamilyId = computed(() => currentFamily.value?.id || null)
  const currentFamilyName = computed(() => currentFamily.value?.name || '')

  const hasMembers = computed(() => currentFamilyMembers.value.length > 0)
  const membersCount = computed(() => currentFamilyMembers.value.length)

  const isLoading = computed(() =>
    loadingFamilies.value ||
    loadingFamily.value ||
    loadingMembers.value ||
    creatingFamily.value ||
    updatingFamily.value ||
    deletingFamily.value ||
    creatingMember.value ||
    updatingMember.value ||
    deletingMember.value
  )

  const hasError = computed(() => error.value !== null)

  // Getters para buscar familias específicas
  const getFamilyById = computed(() => {
    return (id: number) => families.value.find(family => family.id === id)
  })

  const getMemberById = computed(() => {
    return (id: number) => currentFamilyMembers.value.find(member => member.id === id)
  })

  // ============== ACTIONS - FAMILIAS ==============

  /**
   * Sustituye una familia de la lista sin perder su contador de miembros: si la respuesta no lo
   * trae, se conserva el que había. Sin esto, la tarjeta caía al `?? 0` y decía «0 miembros».
   */
  const replaceFamilyInList = (family: Family): void => {
    const index = families.value.findIndex(f => f.id === family.id)
    if (index === -1) return
    const previous = families.value[index]
    families.value[index] = { ...family, memberCount: family.memberCount ?? previous.memberCount }
  }

  /**
   * Fija el contador de miembros de una familia en la lista y en la actual, para que la tarjeta
   * diga lo mismo que la lista de miembros que tiene debajo.
   */
  const setFamilyMemberCount = (familyId: number, count: number): void => {
    const index = families.value.findIndex(f => f.id === familyId)
    if (index !== -1) {
      families.value[index] = { ...families.value[index], memberCount: count }
    }
    if (currentFamily.value?.id === familyId) {
      currentFamily.value = { ...currentFamily.value, memberCount: count }
    }
  }

  const memberCountOf = (familyId: number): number =>
    families.value.find(f => f.id === familyId)?.memberCount ?? 0

  /**
   * Carga todas las familias del usuario usando caso de uso del dominio
   */
  const loadFamilies = async (): Promise<void> => {
    if (loadingFamilies.value) return // Evitar llamadas duplicadas

    loadingFamilies.value = true
    clearError()

    try {
      const fetchedFamilies = await loadFamiliesUseCase()
      families.value = fetchedFamilies
    } catch (err) {
      handleError(err as ApiError, 'Error al cargar las familias')
    } finally {
      loadingFamilies.value = false
    }
  }

  /**
   * Carga una familia específica por ID usando caso de uso del dominio
   */
  const loadFamily = async (id: number): Promise<void> => {
    loadingFamily.value = true
    clearError()

    try {
      const family = await loadFamilyUseCase(id)
      currentFamily.value = family

      // También actualizar en la lista si existe
      replaceFamilyInList(family)
    } catch (err) {
      handleError(err as ApiError, 'Error al cargar la familia')
    } finally {
      loadingFamily.value = false
    }
  }

  /**
   * Crea una nueva familia usando caso de uso del dominio
   */
  const createFamily = async (familyData: CreateFamilyRequest): Promise<Family | null> => {
    creatingFamily.value = true
    clearError()

    try {
      const newFamily = await createFamilyUseCase(familyData)

      // Agregar a la lista
      families.value.unshift(newFamily)

      // Establecer como familia actual
      currentFamily.value = newFamily

      // Mostrar toast de éxito
      toast.familySuccess('created', newFamily.name)
      showSuccess('Familia creada exitosamente')
      return newFamily
    } catch (err) {
      // Mostrar toast de error
      toast.familyError('create', (err as ApiError).message)
      handleError(err as ApiError, 'Error al crear la familia')
      return null
    } finally {
      creatingFamily.value = false
    }
  }

  /**
   * Actualiza una familia existente usando caso de uso del dominio
   */
  const updateFamily = async (id: number, familyData: UpdateFamilyRequest): Promise<Family | null> => {
    updatingFamily.value = true
    clearError()

    try {
      const updatedFamily = await updateFamilyUseCase(id, familyData)

      // Actualizar en la lista
      replaceFamilyInList(updatedFamily)

      // Actualizar familia actual si es la misma
      if (currentFamily.value?.id === id) {
        currentFamily.value = updatedFamily
      }

      // Mostrar toast de éxito
      toast.familySuccess('updated', updatedFamily.name)
      showSuccess('Familia actualizada exitosamente')
      return updatedFamily
    } catch (err) {
      // Mostrar toast de error
      toast.familyError('update', (err as ApiError).message)
      handleError(err as ApiError, 'Error al actualizar la familia')
      return null
    } finally {
      updatingFamily.value = false
    }
  }

  /**
   * Elimina una familia usando caso de uso del dominio
   */
  const deleteFamily = async (id: number): Promise<boolean> => {
    deletingFamily.value = true
    clearError()

    // Obtener nombre antes de eliminar para el toast
    const familyName = families.value.find(f => f.id === id)?.name || 'Familia'

    try {
      await deleteFamilyUseCase(id)

      // Remover de la lista
      families.value = families.value.filter(f => f.id !== id)

      // Limpiar familia actual si es la misma
      if (currentFamily.value?.id === id) {
        currentFamily.value = null
        currentFamilyMembers.value = []
      }

      // Mostrar toast de éxito
      toast.familySuccess('deleted', familyName)
      showSuccess('Familia eliminada exitosamente')
      return true
    } catch (err) {
      // Mostrar toast de error
      toast.familyError('delete', (err as ApiError).message)
      handleError(err as ApiError, 'Error al eliminar la familia')
      return false
    } finally {
      deletingFamily.value = false
    }
  }

  /**
   * Carga los roles disponibles para familias usando caso de uso del dominio
   */
  const loadRoles = async (): Promise<void> => {
    if (loadingRoles.value || roles.value.length > 0) return // Cache simple

    loadingRoles.value = true
    clearError()

    try {
      const fetchedRoles = await loadFamilyRolesUseCase()
      roles.value = fetchedRoles
    } catch (err) {
      handleError(err as ApiError, 'Error al cargar los roles')
    } finally {
      loadingRoles.value = false
    }
  }

  // ============== ACTIONS - MIEMBROS ==============

  /**
   * Carga los miembros de una familia usando caso de uso del dominio
   */
  const loadMembers = async (familyId?: number): Promise<void> => {
    const targetId = familyId || currentFamilyId.value
    if (!targetId) return

    loadingMembers.value = true
    clearError()

    try {
      const members = await loadFamilyMembersUseCase(targetId)
      currentFamilyMembers.value = members
      setFamilyMemberCount(targetId, members.length)
    } catch (err) {
      handleError(err as ApiError, 'Error al cargar los miembros')
    } finally {
      loadingMembers.value = false
    }
  }

  /**
   * Crea un nuevo miembro en la familia actual usando caso de uso del dominio
   */
  const createMember = async (memberData: CreateMemberRequest, familyId?: number): Promise<FamilyMember | null> => {
    const targetId = familyId || currentFamilyId.value
    if (!targetId) return null

    creatingMember.value = true
    clearError()

    try {
      const newMember = await createMemberUseCase(targetId, memberData)

      // Agregar a la lista de miembros si es la familia actual
      if (targetId === currentFamilyId.value) {
        currentFamilyMembers.value.push(newMember)
      }
      setFamilyMemberCount(targetId, memberCountOf(targetId) + 1)

      // Mostrar toast de éxito
      const familyName = currentFamily.value?.name || 'la familia'
      toast.familySuccess('memberAdded', familyName)
      showSuccess('Miembro agregado exitosamente')
      return newMember
    } catch (err) {
      // Mostrar toast de error
      toast.familyError('memberAdd', (err as ApiError).message)
      handleError(err as ApiError, 'Error al agregar el miembro')
      return null
    } finally {
      creatingMember.value = false
    }
  }

  /**
   * Actualiza un miembro existente usando caso de uso del dominio
   */
  const updateMember = async (id: number, memberData: UpdateMemberRequest): Promise<FamilyMember | null> => {
    updatingMember.value = true
    clearError()

    try {
      const updatedMember = await updateMemberUseCase(id, memberData)

      // Actualizar en la lista de miembros actuales
      const index = currentFamilyMembers.value.findIndex(m => m.id === id)
      if (index !== -1) {
        currentFamilyMembers.value[index] = updatedMember
      }

      // Mostrar toast de éxito
      toast.familySuccess('memberUpdated')
      showSuccess('Miembro actualizado exitosamente')
      return updatedMember
    } catch (err) {
      // Mostrar toast de error
      toast.familyError('memberUpdate', (err as ApiError).message)
      handleError(err as ApiError, 'Error al actualizar el miembro')
      return null
    } finally {
      updatingMember.value = false
    }
  }

  /**
   * Elimina (expulsa) un miembro usando caso de uso del dominio.
   *
   * EP-26 (B7.5, mr-12): desde B6 el backend puede rechazar con motivos
   * concretos (`MEMBER_REMOVAL_LAST_HOLDER`, `..._SELF_NOT_ALLOWED`,
   * `..._ALREADY_PENDING`). El toast usa `describeMemberRemovalError` para
   * mostrar ese motivo específico en vez del texto crudo del backend
   * cuando el `errorCode` es uno de los tres conocidos; si no lo es, cae al
   * mensaje del backend (comportamiento previo, sin regresión).
   */
  const deleteMember = async (id: number): Promise<boolean> => {
    deletingMember.value = true
    clearError()

    try {
      await deleteMemberUseCase(id)

      // Remover de la lista de miembros actuales
      currentFamilyMembers.value = currentFamilyMembers.value.filter(m => m.id !== id)
      if (currentFamilyId.value) {
        setFamilyMemberCount(currentFamilyId.value, currentFamilyMembers.value.length)
      }

      // Mostrar toast de éxito
      toast.familySuccess('memberDeleted')
      showSuccess('Miembro eliminado exitosamente')
      return true
    } catch (err) {
      const apiError = err as ApiError
      // Mostrar toast de error
      toast.familyError('memberDelete', describeMemberRemovalError(apiError.errorCode, apiError.message))
      handleError(apiError, 'Error al eliminar el miembro')
      return false
    } finally {
      deletingMember.value = false
    }
  }

  // ============== UTILITY ACTIONS ==============

  /**
   * ADR-019 D5 (fase 3): la familia que se elige en el selector se recuerda en ESTE navegador, para
   * que una recarga no vuelva a la del login. Es una comodidad por persona y navegador, no un dato:
   * si `localStorage` falta o falla (navegación privada), o la elegida ya no es de la cuenta, se usa
   * la familia por defecto que manda el servidor. Todo acceso va en try/catch por eso.
   */
  const CLAVE_FAMILIA_ELEGIDA = 'pepi_familia_elegida'

  const leerFamiliaElegida = (): number | null => {
    try {
      const valor = Number(localStorage.getItem(CLAVE_FAMILIA_ELEGIDA))
      return Number.isInteger(valor) && valor > 0 ? valor : null
    } catch {
      return null
    }
  }

  const recordarFamiliaElegida = (familyId: number | null): void => {
    try {
      if (familyId == null) {
        localStorage.removeItem(CLAVE_FAMILIA_ELEGIDA)
      } else {
        localStorage.setItem(CLAVE_FAMILIA_ELEGIDA, String(familyId))
      }
    } catch {
      // Sin almacenamiento: la próxima vez se usará la familia por defecto del servidor.
    }
  }

  /**
   * Establece una familia como la familia actual, y la recuerda (ADR-019 D5).
   */
  const setCurrentFamily = (family: Family | null): void => {
    currentFamily.value = family
    if (family) {
      recordarFamiliaElegida(family.id ?? null)
      // Cargar miembros automáticamente al cambiar familia
      loadMembers(family.id)
    } else {
      currentFamilyMembers.value = []
    }
  }

  /**
   * Garantiza que haya una familia activa seleccionada.
   *
   * Pensado para el arranque / rehidratación de sesión: las vistas sin
   * `:id` en la URL (chat, dashboard, tareas, calendario) necesitan el
   * familyId real del usuario sin recurrir a un fallback hardcodeado.
   *
   * ADR-019 D5: si en este navegador se eligió otra familia y sigue siendo de la cuenta, gana esa;
   * para comprobarlo hace falta la lista, que solo se pide cuando la elegida no es la preferida.
   *
   * @param preferred Familia preferida (la que el backend devuelve en login: la
   *                  familia por defecto). Si no se pasa y aún no hay familia
   *                  activa, cae a la primera familia del usuario.
   */
  const ensureActiveFamily = async (preferred?: Family | null): Promise<void> => {
    if (currentFamily.value) return

    const elegida = leerFamiliaElegida()
    if (elegida != null && elegida !== preferred?.id) {
      if (families.value.length === 0) {
        await loadFamilies()
      }
      const suya = families.value.find((f) => f.id === elegida)
      if (suya) {
        setCurrentFamily(suya)
        return
      }
    }

    if (preferred) {
      setCurrentFamily(preferred)
      return
    }

    if (families.value.length === 0) {
      await loadFamilies()
    }
    if (families.value.length > 0) {
      setCurrentFamily(families.value[0])
    }
  }

  /**
   * Maneja errores de forma consistente.
   *
   * EP-26 (B7.5, mr-12): propaga `errorCode` además del mensaje, para que
   * cualquier llamante que lea `error.value` directamente (no solo el
   * toast) pueda distinguir el motivo real sin volver a parsear la
   * respuesta HTTP.
   */
  const handleError = (err: ApiError, fallbackMessage: string): void => {
    error.value = {
      message: err.message || fallbackMessage,
      status: err.status || 500,
      timestamp: err.timestamp || new Date().toISOString(),
      path: err.path || '',
      errorCode: err.errorCode
    }
  }

  /**
   * Muestra un mensaje de éxito temporal
   */
  const showSuccess = (message: string): void => {
    successMessage.value = message
    // Auto-limpiar después de 3 segundos
    setTimeout(() => {
      successMessage.value = null
    }, 3000)
  }

  /**
   * Limpia el error actual
   */
  const clearError = (): void => {
    error.value = null
  }

  /**
   * Limpia el mensaje de éxito
   */
  const clearSuccess = (): void => {
    successMessage.value = null
  }

  /**
   * Reset completo del store
   */
  const reset = (): void => {
    families.value = []
    currentFamily.value = null
    // Cerrar sesión olvida la familia elegida: quien entre después en este navegador no la hereda.
    recordarFamiliaElegida(null)
    currentFamilyMembers.value = []
    roles.value = []
    error.value = null
    successMessage.value = null

    // Reset loading states
    loadingFamilies.value = false
    loadingFamily.value = false
    loadingMembers.value = false
    loadingRoles.value = false
    creatingFamily.value = false
    updatingFamily.value = false
    deletingFamily.value = false
    creatingMember.value = false
    updatingMember.value = false
    deletingMember.value = false
  }

  /**
   * Inicialización del store - carga datos básicos
   */
  const initialize = async (): Promise<void> => {
    await Promise.all([
      loadFamilies(),
      loadRoles()
    ])
  }

  // ============== RETURN ==============

  return {
    // State
    families,
    currentFamily,
    currentFamilyMembers,
    roles,
    error,
    successMessage,

    // Loading states
    loadingFamilies,
    loadingFamily,
    loadingMembers,
    loadingRoles,
    creatingFamily,
    updatingFamily,
    deletingFamily,
    creatingMember,
    updatingMember,
    deletingMember,

    // Getters
    hasFamilies,
    familiesCount,
    hasCurrentFamily,
    currentFamilyId,
    currentFamilyName,
    hasMembers,
    membersCount,
    isLoading,
    hasError,
    getFamilyById,
    getMemberById,

    // Actions - Familias
    loadFamilies,
    loadFamily,
    createFamily,
    updateFamily,
    deleteFamily,
    loadRoles,

    // Actions - Miembros
    loadMembers,
    createMember,
    updateMember,
    deleteMember,

    // Utility Actions
    setCurrentFamily,
    ensureActiveFamily,
    clearError,
    clearSuccess,
    reset,
    initialize
  }
})